import { uuid } from "~/lib/utils"
import { PluginReviewActionNotAllowedError } from "~/lib/AjaxCtx"

export const getters = {
    "tnt/getLastVersion": state => (userActivityId, idFile) => {
        const activity = state.userActivities[userActivityId]
        const file = activity.state.files.find(f => f.tntFileId == idFile)
        return file.versions.at(-1)
    },
}

export const mutations = {
    "tnt/updateLocalFileProgress"(state, { userActivityId, key, progress }) {
        const activity = state.userActivities[userActivityId]
        const file = activity.localState.files.find(f => f.key == key)
        file.progress = progress
    },

    "tnt/accessTeacherVersion"(state, { userActivityId, versionId }) {
        const activity = state.userActivities[userActivityId]
        const version = activity.state.versions.find(
            v => v.tntVersionId == versionId
        )
        version.accessed = true
    },

    "tnt/removeLocalFile"(state, { userActivityId, key }) {
        const activity = state.userActivities[userActivityId]
        const fileIdx = activity.localState.files.findIndex(f => f.key == key)
        if (fileIdx === -1) {
            return
        }
        activity.localState.files.splice(fileIdx, 1)
    },

    "tnt/pushFile"(state, { userActivityId, file }) {
        const activity = state.userActivities[userActivityId]
        activity.state.files.push(file)
    },

    "tnt/pushVersion"(state, { userActivityId, idFile, version }) {
        const activity = state.userActivities[userActivityId]
        const file = activity.state.files.find(f => f.tntFileId == idFile)
        file.versions.push(version)
    },

    "tnt/removeFile"(state, { userActivityId, idFile }) {
        const activity = state.userActivities[userActivityId]
        const fileIdx = activity.state.files.findIndex(
            f => f.tntFileId == idFile
        )
        if (fileIdx === -1) return
        activity.state.files.splice(fileIdx, 1)
    },

    "tnt/removeVersion"(state, { userActivityId, idFile, idVersion }) {
        const activity = state.userActivities[userActivityId]
        const file = activity.state.files.find(f => f.tntFileId == idFile)
        const versionIdx = file.versions.findIndex(
            v => v.tntVersionId == idVersion
        )
        if (versionIdx === -1) return
        file.versions.splice(versionIdx, 1)
    },

    "tnt/pushComment"(state, { userActivityId, idFile, comment }) {
        const activity = state.userActivities[userActivityId]
        const file = activity.state.files.find(f => f.tntFileId == idFile)
        file.versions.at(-1).comments.push(comment)
    },
}

export function makeActions(service) {
    return {
        async "tnt/addVersion"(
            { state, commit, dispatch },
            { userActivityId, tntFileId, webFile }
        ) {
            const activity = state.userActivities[userActivityId]

            const localFile = {
                tntFileId,
                key: uuid(),
                name: webFile.name,
                size: webFile.size,
                type: webFile.type,
                progress: 0,
            }

            commit("setLocalState", {
                userActivityId,
                localState: {
                    files: (activity.localState.files || []).concat([
                        localFile,
                    ]),
                },
            })

            try {
                const descriptor = await service.uploadFile(
                    webFile,
                    (loaded, total) => {
                        commit("tnt/updateLocalFileProgress", {
                            userActivityId,
                            key: localFile.key,
                            progress: loaded / total,
                        })
                    }
                )
                commit("tnt/updateLocalFileProgress", {
                    userActivityId,
                    key: localFile.key,
                    progress: 1,
                })

                const newVersion = await service.addVersion(
                    userActivityId,
                    tntFileId,
                    webFile.name,
                    descriptor.fileHash
                )

                commit("setLocalState", {
                    userActivityId,
                    localState: {
                        files: activity.localState.files.filter(
                            f => f.key != localFile.key
                        ),
                    },
                })

                commit("tnt/pushVersion", {
                    userActivityId,
                    idFile: tntFileId,
                    version: newVersion,
                })
            } catch (error) {
                commit("setLocalState", {
                    userActivityId,
                    localState: {
                        files: activity.localState.files.filter(
                            f => f.key != localFile.key
                        ),
                    },
                })
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                throw error
            }
        },

        async "tnt/addFile"(
            { state, commit, dispatch },
            { userActivityId, webFile }
        ) {
            const activity = state.userActivities[userActivityId]

            const localFile = {
                tntFileId: -1,
                key: uuid(),
                name: webFile.name,
                size: webFile.size,
                type: webFile.type,
                progress: 0,
            }

            commit("setLocalState", {
                userActivityId,
                localState: {
                    files: (activity.localState.files || []).concat([
                        localFile,
                    ]),
                },
            })

            try {
                const descriptor = await service.uploadFile(
                    webFile,
                    (loaded, total) => {
                        commit("tnt/updateLocalFileProgress", {
                            userActivityId,
                            key: localFile.key,
                            progress: loaded / total,
                        })
                    }
                )
                commit("tnt/updateLocalFileProgress", {
                    userActivityId,
                    key: localFile.key,
                    progress: 1,
                })

                const newFile = await service.addFile(
                    userActivityId,
                    webFile.name,
                    descriptor.fileHash
                )

                commit("tnt/removeLocalFile", {
                    userActivityId,
                    key: localFile.key,
                })
                commit("tnt/pushFile", { userActivityId, file: newFile })
            } catch (error) {
                commit("tnt/removeLocalFile", {
                    userActivityId,
                    key: localFile.key,
                })
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                throw error
            }
        },

        async "tnt/deleteFile"(
            { commit, dispatch },
            { userActivityId, idFile }
        ) {
            try {
                await service.deleteFile(userActivityId, idFile)
                commit("tnt/removeFile", { userActivityId, idFile })
            } catch (error) {
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                throw error
            }
        },

        async "tnt/deleteVersion"(
            { commit, dispatch },
            { userActivityId, idFile, idVersion }
        ) {
            try {
                await service.deleteVersion(userActivityId, idVersion)
                commit("tnt/removeVersion", {
                    userActivityId,
                    idFile,
                    idVersion,
                })
            } catch (error) {
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                throw error
            }
        },

        async "tnt/publish"({ commit, dispatch }, { userActivityId }) {
            try {
                const newState = await service.publish(userActivityId)
                commit("setState", { userActivityId, activityState: newState })
            } catch (error) {
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                throw error
            }
        },

        async "tnt/sendComment"(
            { getters, commit, dispatch },
            { userActivityId, idFile, comment }
        ) {
            try {
                const version = getters["tnt/getLastVersion"](
                    userActivityId,
                    idFile
                )
                const newComment = await service.sendComment(
                    userActivityId,
                    version.tntVersionId,
                    comment
                )
                commit("tnt/pushComment", {
                    userActivityId,
                    idFile,
                    comment: newComment,
                })
            } catch (error) {
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                throw error
            }
        },

        "tnt/accessTeacherVersion"({ commit }, { userActivityId, versionId }) {
            commit("tnt/accessTeacherVersion", { userActivityId, versionId })
        },
    }
}

export default {
    getters,
    mutations,
    makeActions,
}
